import { useState } from "react";
import { Typography, Dialog, Box, Modal } from "@mui/material";
import { toast } from "react-toastify";
import { UserAuth } from "provider/AuthProvider";
import { BASE_URL } from "lib/utilities/globalConstants";
import { Cancel } from "@mui/icons-material";
import CloseJobFormTemplate from "customComponents/AdminComponents/CloseJobFormTemplate";

const CloseFormModal = ({ formId, isClosedForm, setIsClosedForm, requiredVariables, requiredVariableDesc }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [strExists, setStrExists] = useState(false);
  const [templatesValid, setTemplatesValid] = useState(true);
  const [openSubmissionModal, setOpenSubmissionModal] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [missingVariables, setMissingVariables] = useState({
    closeJobFormTemplate: [],
  });

  const { user, setWallet, formData, setFormData } = UserAuth();

  const handleTemplateChange = (e) => {
    const { id, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      templatesV1: { ...prevFormData.templatesV1, [id]: value },
    }));
  };

  function handleSubmissionModalClose() {
    setOpenSubmissionModal(false);
  }

  function handleSubmissionModalProceed() {
    setOpenSubmissionModal(false);
    submitCloseForm();
  }

  const handleClose = async () => {
    if (!sendEmail || templatesValid) {
      submitCloseForm();
    } else {
      setOpenSubmissionModal(true);
    }
  };

  const submitCloseForm = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${BASE_URL}admin/closeJobForm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.accessToken}`,
        },
        body: JSON.stringify({
          formId: formId,
          sendEmail: sendEmail,
          templates: sendEmail
            ? {
              closeJobFormTemplate: formData.templatesV1.closeJobFormTemplate,
              closeJobFormSubject: formData.templatesV1.closeJobFormSubject,
            }
            : {},
        }),
      });
      const data = await response.json();
      if (response.status === 200) {
        if (data.success) {
          toast.success(
            `Job form closed successfully. Released ${data.data.remainingCoins} coins back to your account.`
          );
          setWallet({
            Coins: data.data.numberOfCoins,
            Hold: data.data.coins_hold,
            Spent: data.data.coins_spent,
          });
          setIsClosedForm(1);
        } else {
          toast.error(data.message || "Failed to close job form");
        }
      } else {
        throw new Error(data.message || "Failed to close job form");
      }
    } catch (error) {
      toast.error("Fail! Try Again.");
    } finally {
      setIsLoading(false);
      setOpenModal(false);
    }
  }

  return (
    <>
      <button
        type="button"
        disabled={isClosedForm}
        onClick={() => setOpenModal(true)}
        className="flex items-center justify-center px-4 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200 border border-red-200 disabled:cursor-not-allowed disabled:opacity-70"
      >
        <span className="mr-2">{isClosedForm ? "Closed" : "Close Form"}</span>
        <Cancel style={{ height: "16px", width: "16px" }} />
      </button>
      <Dialog
        maxWidth="md"
        open={openModal}
        fullWidth
        onClose={() => !isLoading && setOpenModal(false)}
      >
        <div className="overflow-hidden flex flex-col">
          <div className="flex items-center justify-between border-b py-2 px-5 sticky top-0 bg-white z-50">
            <Typography variant="h5" className="font-semibold text-gray-900">
              Close Job Form
            </Typography>
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => setOpenModal(false)}
                className="text-blue-700 text-sm font-medium bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors px-4 py-1.5 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="text-red-600 text-sm font-medium bg-red-50 border border-red-200 hover:bg-red-100 transition-colors px-4 py-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Closing..." : "Close Form"}
              </button>
            </div>
          </div>
          <div className="flex flex-col overflow-auto">
            <div className="p-5 space-y-6">
              <div className="rounded-xl overflow-hidden border border-red-200">
                <div className="bg-red-100 px-4 py-3 border-b border-red-200">
                  <h3 className="font-semibold text-lg text-red-800 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    What happens when you close this form?
                  </h3>
                </div>
                <div className="bg-white px-4 py-3">
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700 text-base">Release any remaining hold amount back to your account</span>
                    </li>
                    <li className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700 text-base">Mark the job form as closed</span>
                    </li>
                    {sendEmail && (
                      <li className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700 text-base">Send a notification email to all candidates about the job closure</span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="bg-blue-50 p-2 rounded-lg mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-800">Email Notification</h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Send a notification email to all candidates about the job closure
                      </p>
                    </div>
                  </div>
                  <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out">
                    <input
                      type="checkbox"
                      id="sendEmailToggle"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      className="opacity-0 w-0 h-0"
                    />
                    <label
                      htmlFor="sendEmailToggle"
                      className={`absolute cursor-pointer top-0 left-0 right-0 bottom-0 rounded-full transition-colors duration-300 ${sendEmail ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                    >
                      <span
                        className={`absolute left-1 bottom-1 bg-white rounded-full w-4 h-4 transition-transform duration-300 ${sendEmail ? 'transform translate-x-6' : ''
                          }`}
                      ></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            {sendEmail && (
              <CloseJobFormTemplate
                templates={{ closeJobFormTemplate: formData.templatesV1.closeJobFormTemplate, closeJobFormSubject: formData.templatesV1.closeJobFormSubject }}
                handleTemplateChange={handleTemplateChange}
                setTemplatesValid={setTemplatesValid}
                requiredVariables={requiredVariables}
                requiredVariableDesc={requiredVariableDesc}
                missingVariables={missingVariables}
                setMissingVariables={setMissingVariables}
                setStrExists={setStrExists}
                strExists={strExists}
              />
            )}
            <Modal open={openSubmissionModal} onClose={handleSubmissionModalClose}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 600,
                  bgcolor: "background.paper",
                  border: "1px solid black",
                  boxShadow: 24,
                  p: 4,
                  borderRadius: 2,
                }}
              >
                {Object.values(missingVariables).some((v) => v.length > 0) && (
                  <>
                    <h2 className="text-red-600 font-bold text-center mb-4">
                      Missing Variables Detected
                    </h2>
                    <p className="text-center text-gray-600 mb-6">
                      Some templates are missing required variables. Do you want to proceed with
                      submission?
                      <ul>
                        {Object.entries(missingVariables).map(([templateId, missingVars]) =>
                          missingVars.length > 0 ? (
                            <li key={templateId}>
                              <b>{templateId}:</b> Missing "{missingVars.join('", "')}"
                            </li>
                          ) : null
                        )}
                      </ul>
                    </p>
                  </>
                )}
                {Object.values(strExists).some((s) => s.length > 0) && (
                  <>
                    <h2 className="text-red-600 font-bold text-center mb-1">
                      Missing Essential Information
                    </h2>
                    <p className="text-center text-sm text-red-600 mb-5">
                      *Some templates are missing essential information, such as the Job Role and
                      Company Name. Please ensure all fields are complete before submitting.*
                      <ul className="text-gray-800 text-base">
                        {Object.entries(strExists).map(([templateId, str]) =>
                          str.length > 0 ? (
                            <li key={templateId}>
                              <b>{templateId}:</b> Missing "{str.join('", "')}"
                            </li>
                          ) : null
                        )}
                      </ul>
                    </p>
                  </>
                )}
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <button
                    type="button"
                    onClick={handleSubmissionModalClose}
                    className="text-blue-700 text-sm font-medium bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors px-4 py-1.5 flex items-center rounded-lg"
                  >
                    Go Back
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmissionModalProceed}
                    className="flex items-center justify-center px-4 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200 border border-red-200"
                  >
                    Submit Anyway
                  </button>
                </Box>
              </Box>
            </Modal>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default CloseFormModal;
